import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Typography, Button, Skeleton, Chip, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import styles from './Offer.module.scss';
import { PageLayout } from 'lib';
import { OffersAPI, UsersAPI, CompaniesAPI } from 'api_inbusiness';
import { useUser } from 'hooks/useUser';
import { toast } from 'react-toastify';
import EditOfferModal from './EditOfferModal';
import DeleteOfferModal from './DeleteOfferModal';

const Offer = () => {
  const { id } = useParams();
  const { user } = useUser();
  const history = useHistory();
  
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorAvatar, setAuthorAvatar] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [offerImage, setOfferImage] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    loadOffer();
  }, [id]);

  const loadOffer = async () => {
    setLoading(true);
    setCompanyLogo(null);
    const res = await OffersAPI.getById(id);
    if (res.ok) {
      setOffer(res.data);
      // Load author avatar
      if (res.data.user?.avatarId) {
        const { data: imgUrl } = await UsersAPI.getNewUserPicture(res.data.user.avatarId);
        setAuthorAvatar(imgUrl);
      }
      // Load company logo
      if (res.data.company?.logoId) {
        const { data: logoUrl } = await CompaniesAPI.getNewLogoCompanies(res.data.company.logoId);
        setCompanyLogo(logoUrl);
      }
      // Check if offer has custom image
      const imageCheck = await OffersAPI.checkImageExists(res.data.id);
      if (imageCheck.ok && imageCheck.exists) {
        setOfferImage(OffersAPI.getImageUrl(res.data.id, Date.now()));
      } else {
        setOfferImage(null);
      }
    } else {
      toast.error('Oferta nu a fost găsită');
      history.push('/offers');
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isOwner = user?.id === offer?.userId;
  const isAdmin = ['LEADER', 'MANAGER'].includes(user?.type);

  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    setDeleteModalOpen(false);
    toast.success('Oferta a fost ștearsă');
    history.push('/offers');
  };

  if (loading) {
    return (
      <PageLayout title="Ofertă" hasBackButton backUrl="/offers">
        <div className={styles.container}>
          <Skeleton variant="rectangular" height={300} className={styles.skeleton} />
          <Skeleton variant="text" height={40} width="60%" />
          <Skeleton variant="text" height={24} width="40%" />
          <Skeleton variant="rectangular" height={150} className={styles.skeleton} />
        </div>
      </PageLayout>
    );
  }

  if (!offer) {
    return null;
  }

  return (
    <PageLayout 
      title={offer.title} 
      backArrow 
      backUrl="/offers"
      actionArea={
        (isOwner || isAdmin) && (
          <div className={styles.actionButtons}>
            <IconButton onClick={() => setEditModalOpen(true)} className={styles.editBtn}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={handleDelete} className={styles.deleteBtn}>
              <DeleteIcon />
            </IconButton>
          </div>
        )
      }
    >
      <div className={styles.container}>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          {offer.discount && (
            <div className={styles.discountBadge}>
              <span className={styles.discountLabel}>REDUCERE</span>
              <span className={styles.discountValue}>{offer.discount}</span>
            </div>
          )}
          
          <div className={styles.heroContent}>
            {offer.category && (
              <Chip label={offer.category} className={styles.categoryChip} />
            )}
            <Typography variant="h3" className={styles.title}>
              {offer.title}
            </Typography>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.contentGrid}>
          {/* Left Column - Details */}
          <div className={styles.mainContent}>
            {/* Description Card */}
            <div className={styles.card}>
              <Typography variant="h6" className={styles.cardTitle}>
                Descriere
              </Typography>
              <Typography variant="body1" className={styles.description}>
                {offer.description}
              </Typography>
              
              {/* Offer Image - below description */}
              {offerImage && (
                <div className={styles.offerImageContainer}>
                  <img 
                    src={offerImage} 
                    alt={offer.title} 
                    className={styles.offerImage}
                  />
                </div>
              )}
            </div>

            {/* Terms Card */}
            {offer.terms && (
              <div className={styles.card}>
                <Typography variant="h6" className={styles.cardTitle}>
                  Termeni și Condiții
                </Typography>
                <Typography variant="body2" className={styles.terms}>
                  {offer.terms}
                </Typography>
              </div>
            )}

            {/* Validity */}
            {(offer.validFrom || offer.validUntil) && (
              <div className={styles.card}>
                <Typography variant="h6" className={styles.cardTitle}>
                  Valabilitate
                </Typography>
                <div className={styles.validityInfo}>
                  <CalendarTodayIcon />
                  <span>
                    {offer.validFrom && `De la ${formatDate(offer.validFrom)}`}
                    {offer.validFrom && offer.validUntil && ' - '}
                    {offer.validUntil && `Până la ${formatDate(offer.validUntil)}`}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className={styles.sidebar}>
            {/* Author Card */}
            <div className={styles.sidebarCard}>
              <Typography variant="h6" className={styles.cardTitle}>
                Oferit de
              </Typography>
              <div className={styles.authorInfo}>
                {authorAvatar ? (
                  <img src={authorAvatar} alt="Avatar" className={styles.authorAvatar} />
                ) : (
                  <div className={styles.authorAvatarPlaceholder}>
                    {offer.user?.firstName?.[0]}{offer.user?.lastName?.[0]}
                  </div>
                )}
                <div className={styles.authorDetails}>
                  <Typography variant="subtitle1" className={styles.authorName}>
                    {offer.user?.firstName} {offer.user?.lastName}
                  </Typography>
                  {offer.user?.email && (
                    <a href={`mailto:${offer.user.email}`} className={styles.contactLink}>
                      <EmailIcon /> {offer.user.email}
                    </a>
                  )}
                  {offer.user?.phone && (
                    <a href={`tel:${offer.user.phone}`} className={styles.contactLink}>
                      <PhoneIcon /> {offer.user.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Company Card */}
            {offer.company && (
              <div className={styles.sidebarCard}>
                <Typography variant="h6" className={styles.cardTitle}>
                  Companie
                </Typography>
                <div className={styles.companyInfo}>
                  {companyLogo ? (
                    <img src={companyLogo} alt={offer.company.name} className={styles.companyLogo} />
                  ) : (
                    <BusinessIcon className={styles.companyIcon} />
                  )}
                  <div className={styles.companyDetails}>
                    <Typography variant="subtitle1" className={styles.companyName}>
                      {offer.company.name}
                    </Typography>
                    {offer.company.description && (
                      <Typography variant="body2" color="textSecondary">
                        {offer.company.description.length > 100 
                          ? offer.company.description.substring(0, 100) + '...'
                          : offer.company.description
                        }
                      </Typography>
                    )}
                    {offer.company.site && (
                      <a 
                        href={offer.company.site.startsWith('http') ? offer.company.site : `https://${offer.company.site}`} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={styles.siteLink}
                      >
                        <LanguageIcon /> Vizitează site-ul
                      </a>
                    )}
                    {offer.company.phone && (
                      <a href={`tel:${offer.company.phone}`} className={styles.contactLink}>
                        <PhoneIcon /> {offer.company.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CTA Button */}
            {offer.user?.email && (
              <Button
                variant="contained"
                fullWidth
                className={styles.ctaButton}
                onClick={() => window.location.href = `mailto:${offer.user.email}?subject=Interes pentru oferta: ${offer.title}`}
              >
                Contactează pentru ofertă
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Edit Offer Modal */}
      <EditOfferModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSuccess={() => {
          setEditModalOpen(false);
          loadOffer();
        }}
        offer={offer}
      />

      {/* Delete Offer Modal */}
      <DeleteOfferModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
        offer={offer}
      />
    </PageLayout>
  );
};

export default Offer;
